import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import {Modal, ModalBody} from "../../components/Modal";
import DatePicker from "../../components/DatePicker";
import CustomButton from "../../components/CustomButton";
import LoadingAnimation from "../../components/LoadingAnimation";
import TextLink from "../../components/TextLink";
import taskIcon from "url:shared-assets/icons/task.svg";
import iconDate from "url:shared-assets/icons/calendar.svg";
import iconInfo from "url:shared-assets/icons/info-filled.svg";
import Api from "../../services/Api";
import { storeAiStart } from "../../helpers/ai_thread";
import { getLanguage } from "../../lang/helpers";
import { dateFormatDateYear } from "../../helpers/dateFunctions";
import LocalBreadcrumbs from "../../components/Breadcrumbs/LocalBreadcrumbs";
import { useSetAtom } from 'jotai'
import { toastAtom } from '../../jotai_state'

interface TaskInterface {
  id: string;
  name: string;
  description: string;
  deadline: string;
  completed: boolean;
  focusAreaId: string;
  focusAreaName: string;
}

export default function TaskPageEdit() {
  const api = Api.getInstance();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = getLanguage(i18n.language);
  const [task, setTask] = useState<TaskInterface | null>(null);
  const [title, setTitle] = useState<string>("");
  const [categories, setCategories] = useState(0);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [editDate, setEditDate] = useState<boolean>(false);
  const [deadline, setDeadline] = useState<Date | null>(null);
  const [changed, setChanged] = useState<boolean>(false);
  const setGlobalToast = useSetAtom(toastAtom);

  useEffect(() => {
    getTask();
  }, []);

  useEffect(() => {
    if (changed) {
      save();
    }
  }, [changed]);

  const getTask = () => {
    api.get("client/task/" + taskId).then((response) => {
      setTask(response.json.task);
      setTitle(response.json.task.name);
      setCategories(response.json.categories);
      if (response.json.task.deadline) {
        setDeadline(new Date(response.json.task.deadline));
      }
      setLoaded(true);
    });
  };

  const save = () => {
    const data = {
      title: title,
      deadline: deadline,
    };
    api.post("client/tasks/" + taskId + "/edit", data).then((response) => {
      setGlobalToast({
        text: t("saved-changes"),
      })
      setChanged(false);
    });
  };

  const navigateToEditDescription = () => {
    let message =
      lang === "sv"
        ? "Jag vill ändra beskrivningen av min aktivitet: "
        : "I want to edit the description on my Action: ";

    let chatMessage =
      message +
      title +
      "\n  " +
      (lang === "sv" ? "Beskrivning:\n" : "Description:\n") +
      task?.description;
    storeAiStart(
      "assistant",
      "task",
      task?.focusAreaId,
      task?.id,
      null,
      chatMessage
    );

    navigate("/ai/0");
  };

  const changeDate = (d: Date) => {
    setDeadline(d);
    setChanged(true);
    setEditDate(false);
  };

  if (!loaded) {
    return (
      <div className="w-full h-full flex justify-center mt-4 desktop:mt-6">
        <div className="flex flex-col gap-2 justify-center align-middle w-full desktop:w-151 m-4 mt-0">
          <LoadingAnimation />
        </div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="main-one-col small">
        <div className="mt-6 flex flex-col gap-2">
          <div className="flex justify-center">
            <img
              src={taskIcon}
              alt="task icon"
              className="w-6 h-6 desktop:w-10 desktop:h-10"
            />
          </div>
          <p className="z-body1 text-center">{t("no-tasks")}</p>
          <CustomButton
            className="mt-8"
            onClick={() => navigate("/development-plan")}
          >
            {t("go-to-focus-area")}
          </CustomButton>
        </div>
      </div>
    );
  }

  const showCrumbs = () => {

    let taskName = lang === "sv" ? "Aktivitet" : "Action"

    if (categories > 0) {
      return (
        <LocalBreadcrumbs crumbs={[
          {name: t("long-term-goals"), url: "/dev-plan"},
          {name: taskName, url: "/tasks/" + taskId},
          {name: lang === "sv" ? "Ändra aktivitet" : "Edit action"},
        ]}/>
      )
    }
    return (
      <LocalBreadcrumbs crumbs={[
        {name: lang === "sv" ? "Hem" : "Home", url: "/"},
        {name: lang === "sv" ? "Aktiviteter" : "Activities", url: "/tasks"},
        {name: taskName, url: "/tasks/" + taskId},
        {name: lang === "sv" ? "Ändra aktivitet" : "Edit action"},
      ]}/>
    )
  }

  return (
    <div className="main-content">
      {showCrumbs()}
      <h1 className="z-heading2 text-white mt-4 desktop:mt-6">
        {t("task-edit")}
      </h1>

      <div className="mt-6 font-medium">{t("title-placeholder")}</div>

      <div className="mt-4">
        <input
          type="text"
          className="z-input"
          value={title}
          onBlur={() => setChanged(true)}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <div className="mt-6 font-medium">{t("target-date")}</div>

      <div className="mt-4">
        <div className="z-input">
          <div
            className="flex justify-between cursor-pointer"
            onClick={() => setEditDate(!editDate)}
          >
            <p>{deadline ? dateFormatDateYear(deadline) : "-"}</p>
            <img src={iconDate} alt="date" className="h-4 w-4" />
          </div>
        </div>
      </div>

      <div className="mt-6 font-medium">{t("description")}</div>

      <div className="bg-indigo-800 mt-2 p-4 border-gray-500 border rounded-[6px]">
        <div className="flex">
          <img src={iconInfo} alt="info" />
          <div className="ml-4 font-medium">
            {lang === "sv"
              ? "Beskrivning kan ändras med AI coach"
              : "Description can be edited with AI coach"}
          </div>
        </div>

        <div className="mt-4 text-sm">
          {lang === "sv"
            ? "Det är inte möjligt att ändra aktivitetsbeskrivningen här än, men du kan göra det tillsammans med din AI coach"
            : "It’s not possible to edit Action description here yet, but you can do it together with your AI coach."}
        </div>
        <CustomButton
          buttonType="outlineWhiteBase"
          className="mt-4"
          onClick={() => navigateToEditDescription()}
        >
          {lang === "sv"
            ? "Ändra aktivitet i AI-chatten"
            : "Edit Action in AI chat"}
        </CustomButton>
      </div>

      <TextLink
        className="mt-6"
        color="white"
        onClick={() => navigate("/tasks/" + taskId)}
      >
        {lang === "sv" ? "Tillbaka till aktivitet" : "Back to Action"}
      </TextLink>

      {editDate && (
        <Modal isOpen={true} onClose={() => setEditDate(false)}>
          <ModalBody>
            <DatePicker
              dates={[]}
              clickDate={changeDate}
              preSelectedDate={deadline}
              skipClickMonthChange={true}
              inactiveDatesRange={[new Date()]}
            />
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
